import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FaTrash } from 'react-icons/fa';
import Sidebar from '../../components/Sidebar';
import Topbar from '../../components/Topbar';
import api from '../../../services/api';
import { SERVICES_BY_MODEL } from '../Proposals/Create';

const STEPS = [
  { number: 1, label: 'Contratante' },
  { number: 2, label: 'Serviços' },
  { number: 3, label: 'Valores e pagamento' },
  { number: 4, label: 'Revisão' }
];

const EMPTY_CONTRACTANT = {
  fullName: '',
  cpf: '',
  birthDate: '',
  companyName: '',
  cnpj: '',
  address: '',
  cityState: '',
  email: '',
  phone: ''
};

const EMPTY_PAYMENT = {
  method: 'pix',
  installments: '',
  dueDate: '',
  notes: ''
};

const PAYMENT_METHOD_LABELS = {
  avista: 'À vista',
  pix: 'PIX',
  cartao_credito: 'Cartão de crédito'
};

function Field({ label, name, value, onChange, type = 'text', placeholder = '', min, error = '' }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-500">{label}</span>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        min={min}
        className={`mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-purple-400 focus:ring-2 focus:ring-purple-100 ${error ? 'border-red-400' : 'border-gray-200'}`}
      />
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}

function SectionCard({ number, title, action, children }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-4 flex items-center justify-between border-b border-gray-100 pb-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-purple-50 text-xs text-purple-700">{number}</span>
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function parseCurrencyValue(value) {
  if (value === '' || value == null) return 0;
  if (typeof value === 'number') return value;
  const text = String(value);
  const normalized = text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text;
  return Number(normalized.replace(/[^0-9.-]/g, '')) || 0;
}

function parseProjectDeadline(value) {
  return String(value ?? '').replace(/\D/g, '');
}

function getServiceKey(service) {
  const description = service.title || service.label || service.description || service.desc || '';
  return `${service.id || ''}:${description}`;
}

function normalizeSearchText(value) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function getCurrentDateInputValue() {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${today.getFullYear()}-${month}-${day}`;
}

function getProjectModelLabel(projectModel) {
  if (projectModel == null) return null;

  const rawValue = typeof projectModel === 'object'
    ? (projectModel.title || projectModel.name || projectModel.label || projectModel.model || projectModel.value || '')
    : String(projectModel);

  if (!rawValue) return null;

  const normalized = String(rawValue)
    .toLowerCase()
    .replace(/[-_]/g, ' ')
    .replace(/\s*\+\s*/g, ' ')
    .replace(/\s*\/\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (normalized.includes('interno') && normalized.includes('externo')) {
    return 'Modelagem interna/externa';
  }

  if (normalized.includes('externo')) {
    return 'Modelagem externa';
  }

  if (normalized.includes('interno')) {
    return 'Modelagem interna';
  }

  return null;
}

export default function ContractCreate() {
  const { proposalId } = useParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [proposal, setProposal] = useState(null);
  const [isLoadingProposal, setIsLoadingProposal] = useState(true);
  const [proposalError, setProposalError] = useState(null);
  const [contractant, setContractant] = useState(EMPTY_CONTRACTANT);
  const [payment, setPayment] = useState({ ...EMPTY_PAYMENT, dueDate: getCurrentDateInputValue() });
  const [completed, setCompleted] = useState(false);
  const [preparedPayload, setPreparedPayload] = useState(null);
  const [isSavingContract, setIsSavingContract] = useState(false);
  const [contractError, setContractError] = useState(null);
  const [contractValueInput, setContractValueInput] = useState('');
  const [isEditingValue, setIsEditingValue] = useState(false);
  const [projectDeadlineInput, setProjectDeadlineInput] = useState('');
  const [isEditingProjectDeadline, setIsEditingProjectDeadline] = useState(false);
  const [pixEntryInput, setPixEntryInput] = useState('');
  const [isEditingPixEntry, setIsEditingPixEntry] = useState(false);
  const [showThirdPixInstallment, setShowThirdPixInstallment] = useState(false);
  const [pixInstallment2Input, setPixInstallment2Input] = useState('');
  const [isEditingPixInstallment2, setIsEditingPixInstallment2] = useState(false);
  const [cities, setCities] = useState([]);
  const [citySearch, setCitySearch] = useState('');
  const [isCityListOpen, setIsCityListOpen] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(true);
  const [cityLoadError, setCityLoadError] = useState(null);
  const [citySelectionError, setCitySelectionError] = useState(false);
  const [contractantErrors, setContractantErrors] = useState({ identity: false, document: false });
  const [removedServiceKeys, setRemovedServiceKeys] = useState([]);
  const [addedServices, setAddedServices] = useState([]);
  const [isServiceListOpen, setIsServiceListOpen] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function loadCities() {
      try {
        const response = await fetch('https://servicodados.ibge.gov.br/api/v1/localidades/municipios');
        if (!response.ok) throw new Error('Não foi possível carregar os municípios.');
        const data = await response.json();
        const options = data
          .map(city => ({
            name: city.nome,
            state: city.microrregiao?.mesorregiao?.UF?.sigla || ''
          }))
          .filter(city => city.state)
          .sort((firstCity, secondCity) => firstCity.name.localeCompare(secondCity.name, 'pt-BR'));
        if (mounted) setCities(options);
      } catch (error) {
        if (mounted) setCityLoadError(error.message);
      } finally {
        if (mounted) setIsLoadingCities(false);
      }
    }

    loadCities();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;

    async function loadProposal() {
      setIsLoadingProposal(true);
      setProposalError(null);
      try {
        const userId = localStorage.getItem('user');
        const response = await api.get(`/proposals/${proposalId}`, {
          headers: userId ? { user_id: userId } : undefined
        });
        const data = response.data;
        if (mounted) {
          setProposal(data);
          const proposalValue = data?.project_price ?? data?.projectPrice ?? data?.total ?? data?.total_value ?? data?.totalValue ?? data?.value ?? '';
          setContractValueInput(proposalValue === '' ? '' : String(parseCurrencyValue(proposalValue)));
          const proposalDeadline = data?.projectDeadline ?? data?.project_deadline ?? data?.productionDays ?? data?.production_days ?? data?.deadline ?? data?.deliveryDeadline ?? data?.delivery_deadline ?? '';
          setProjectDeadlineInput(parseProjectDeadline(proposalDeadline));
        }
      } catch (error) {
        if (mounted) setProposalError(error.message || 'Não foi possível carregar o orçamento.');
      } finally {
        if (mounted) setIsLoadingProposal(false);
      }
    }

    loadProposal();
    return () => { mounted = false; };
  }, [proposalId]);

  const handleContractantChange = (event) => {
    const { name, value } = event.target;
    const nextContractant = { ...contractant, [name]: value };
    setContractant(nextContractant);
    setContractantErrors(previous => ({
      identity: previous.identity && !nextContractant.fullName.trim() && !nextContractant.companyName.trim(),
      document: previous.document && !nextContractant.cpf.trim() && !nextContractant.cnpj.trim()
    }));
  };

  const handleCitySearchChange = (event) => {
    const { value } = event.target;
    setCitySearch(value);
    setIsCityListOpen(true);
    setCitySelectionError(false);
    setContractant(previous => ({ ...previous, cityState: '' }));
  };

  const selectCity = (city) => {
    const cityState = `${city.name} - ${city.state}`;
    setCitySearch(cityState);
    setContractant(previous => ({ ...previous, cityState }));
    setIsCityListOpen(false);
    setCitySelectionError(false);
  };

  const removeService = (service) => {
    setRemovedServiceKeys(previous => [...previous, getServiceKey(service)]);
    setAddedServices(previous => previous.filter(item => getServiceKey(item) !== getServiceKey(service)));
  };

  const addService = (service) => {
    setAddedServices(previous => [...previous, { ...service, title: service.label, serviceModel: service.model }]);
    setIsServiceListOpen(false);
  };

  const handlePaymentChange = (event) => {
    const { name, value } = event.target;
    if (name === 'dueDate' && value < getCurrentDateInputValue()) return;
    setPayment(previous => ({ ...previous, [name]: value }));
  };

  const handleContractValueChange = (event) => {
    setContractValueInput(event.target.value.replace(/[^0-9.,]/g, ''));
  };

  const handleProjectDeadlineChange = (event) => {
    setProjectDeadlineInput(parseProjectDeadline(event.target.value));
  };

  const handlePixEntryChange = (event) => {
    setPixEntryInput(event.target.value.replace(/[^0-9.,]/g, ''));
  };

  const handlePixInstallment2Change = (event) => {
    setPixInstallment2Input(event.target.value.replace(/[^0-9.,]/g, ''));
  };

  const getProposalModelValue = (currentProposal) => {
    if (!currentProposal || typeof currentProposal !== 'object') return '';

    const candidateKeys = [
      'project_model',
      'projectModel',
      'model',
      'project_model_name',
      'projectModelName',
      'model_name',
      'type',
      'projectType'
    ];

    for (const key of candidateKeys) {
      const value = currentProposal[key];
      if (typeof value === 'string' && value.trim()) return value;
      if (value && typeof value === 'object') {
        const nestedValue = getProposalModelValue(value);
        if (nestedValue) return nestedValue;
      }
    }

    if (currentProposal.proposal && typeof currentProposal.proposal === 'object') {
      return getProposalModelValue(currentProposal.proposal);
    }

    if (currentProposal.data && typeof currentProposal.data === 'object') {
      return getProposalModelValue(currentProposal.data);
    }

    return '';
  };

  const proposalModelValue = getProposalModelValue(proposal);
  const projectModelLabel = getProjectModelLabel(proposalModelValue);
  const serviceItems = useMemo(() => {
    const rawServiceItems = proposal?.products || proposal?.services || [];
    const proposalServices = [
      ...(projectModelLabel ? [{ id: 'project-model', title: projectModelLabel, label: projectModelLabel, description: projectModelLabel }] : []),
      ...rawServiceItems
    ];
    return [...proposalServices, ...addedServices].filter(service => !removedServiceKeys.includes(getServiceKey(service)));
  }, [addedServices, projectModelLabel, proposal, removedServiceKeys]);
  const getServiceDescription = (service) => service.title || service.label || service.description || service.desc || '';
  const availableContractServices = Object.entries(SERVICES_BY_MODEL).flatMap(([model, services]) => services.map(service => ({ ...service, model })));
  const availableServicesToAdd = availableContractServices.filter(service => !serviceItems.some(item => getServiceKey(item) === getServiceKey(service)));
  const totalValue = proposal?.project_price ?? proposal?.projectPrice ?? proposal?.total ?? proposal?.total_value ?? proposal?.totalValue ?? proposal?.value ?? '';
  const originalProjectDeadline = proposal?.projectDeadline ?? proposal?.project_deadline ?? proposal?.productionDays ?? proposal?.production_days ?? proposal?.deadline ?? proposal?.deliveryDeadline ?? proposal?.delivery_deadline ?? '';
  const normalizedProjectDeadline = (() => {
    if (originalProjectDeadline === '' || originalProjectDeadline == null) return '';
    return parseProjectDeadline(originalProjectDeadline);
  })();
  const originalTotal = parseCurrencyValue(totalValue);
  const total = contractValueInput === '' ? originalTotal : parseCurrencyValue(contractValueInput);
  const isPixPayment = payment.method === 'pix';
  const pixEntryValue = pixEntryInput === '' ? total / (showThirdPixInstallment ? 3 : 2) : parseCurrencyValue(pixEntryInput);
  const pixInstallment2Value = pixInstallment2Input === '' ? (total - pixEntryValue) / 2 : parseCurrencyValue(pixInstallment2Input);
  const pixRemainingValue = showThirdPixInstallment
    ? Math.max(total - pixEntryValue - pixInstallment2Value, 0)
    : Math.max(total - pixEntryValue, 0);
  const filteredCities = cities
    .filter(city => normalizeSearchText(`${city.name} ${city.state}`).includes(normalizeSearchText(citySearch)))
    .slice(0, 50);

  useEffect(() => {
    setPixEntryInput('');
    setPixInstallment2Input('');
    setShowThirdPixInstallment(false);
  }, [total]);

  const contractPayload = useMemo(() => ({
    proposalId,
    contractant,
    proposal: {
      id: proposal?.id || proposalId || '',
      title: proposal?.title || '',
      client: proposal?.client || '',
      services: serviceItems,
      subtotal: proposal?.subtotal || '',
      discount: proposal?.discount || '',
      taxes: proposal?.taxes || '',
      total,
      projectDeadline: projectDeadlineInput || normalizedProjectDeadline || ''
    },
    payment: isPixPayment
      ? {
          ...payment,
          pixEntry: pixEntryValue,
          ...(showThirdPixInstallment ? { pixInstallment2: pixInstallment2Value } : {}),
          pixRemaining: pixRemainingValue
        }
      : payment,
    status: completed ? 'ready-to-generate' : 'draft'
  }), [completed, contractant, isPixPayment, normalizedProjectDeadline, payment, pixEntryValue, pixInstallment2Value, pixRemainingValue, projectDeadlineInput, proposal, proposalId, serviceItems, showThirdPixInstallment, total]);

  const formatCurrency = (value) => {
    if (value === '' || value == null || Number.isNaN(Number(value))) return '—';
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const goNext = () => {
    if (currentStep === 1) {
      const hasIdentity = Boolean(contractant.fullName.trim() || contractant.companyName.trim());
      const hasDocument = Boolean(contractant.cpf.trim() || contractant.cnpj.trim());
      const hasCity = Boolean(contractant.cityState);

      setContractantErrors({ identity: !hasIdentity, document: !hasDocument });
      setCitySelectionError(!hasCity);
      if (!hasCity) setIsCityListOpen(true);

      if (!hasIdentity || !hasDocument || !hasCity) return;
    }
    setCurrentStep(step => Math.min(4, step + 1));
  };
  const goPrevious = () => setCurrentStep(step => Math.max(1, step - 1));
  const finishContract = async () => {
    const userId = localStorage.getItem('user');
    if (!userId) {
      setContractError('Usuário não autenticado localmente. Faça login antes de gerar o contrato.');
      return;
    }
    if (!payment.method) {
      setContractError('Selecione uma forma de pagamento antes de gerar o contrato.');
      return;
    }

    setIsSavingContract(true);
    setContractError(null);
    const payload = { ...contractPayload, status: 'ready-to-generate' };
    console.log('[ContractCreate] Serviços inclusos enviados para criação do contrato:', payload.proposal.services);
    console.table(payload.proposal.services.map((service, index) => ({
      índice: index + 1,
      id: service.id || '',
      serviço: getServiceDescription(service),
      modelo: service.serviceModel || ''
    })));

    try {
      const createResponse = await api.post('/contracts', payload, {
        headers: { user_id: userId, 'Content-Type': 'application/json' }
      });
      const createdContract = createResponse.data;
      const contractId = createdContract?.id;

      if (!contractId) {
        throw new Error('O backend não retornou o identificador do contrato criado.');
      }

      const pdfResponse = await api.get(`/contracts/${contractId}/pdf`, {
        headers: { user_id: userId },
        responseType: 'blob'
      });
      const pdfContentType = pdfResponse.headers['content-type'] || '';
      if (!pdfContentType.includes('application/pdf')) {
        throw new Error('O backend não retornou um arquivo PDF válido.');
      }

      const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const projectTitle = proposal?.title || proposal?.projectName || 'Projeto';
      const contractantName = contractant.fullName || 'Contratante';
      const pdfFileName = `Contrato de Prestação de Serviços | ${contractantName} | ${projectTitle} | M3D STUDIO HOME.pdf`;

      link.href = url;
      link.setAttribute('download', pdfFileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setPreparedPayload(createdContract);
      setCompleted(true);
    } catch (error) {
      const responseData = error.response?.data;
      const details = typeof responseData === 'string'
        ? responseData
        : responseData?.message || (responseData ? JSON.stringify(responseData) : error.message);
      setContractError(`Não foi possível gerar o contrato. ${details}`);
    } finally {
      setIsSavingContract(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1">
        <Topbar user={{ name: 'Mateus Teixeira', role: 'Administrador' }} />
        <main className="mx-auto max-w-7xl p-6">
          <div className="mb-6">
            <Link to="/system/proposals" className="text-sm text-gray-500 hover:text-gray-800">‹ Voltar para orçamentos</Link>
            <h2 className="mt-3 text-2xl font-bold text-gray-800">Novo contrato</h2>
            <p className="mt-2 text-sm text-gray-500">Crie um novo contrato a partir do orçamento {proposalId || '—'}.</p>
          </div>

          <div className="mb-6 flex items-center rounded-xl border border-gray-200 bg-white px-6 py-4">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className={`flex items-center gap-2 text-sm ${currentStep === step.number ? 'font-semibold text-purple-700' : currentStep > step.number ? 'text-gray-700' : 'text-gray-400'}`}>
                  <span className={`flex h-7 w-7 items-center justify-center rounded-full border text-xs ${currentStep >= step.number ? 'border-purple-600 bg-purple-600 text-white' : 'border-gray-300 bg-white'}`}>
                    {step.number}
                  </span>
                  <span>{step.label}</span>
                </div>
                {index < STEPS.length - 1 && <div className={`mx-4 h-px flex-1 ${currentStep > step.number ? 'bg-purple-300' : 'bg-gray-200'}`} />}
              </React.Fragment>
            ))}
          </div>

          {proposalError && <div className="mb-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">{proposalError}</div>}
          {contractError && <div className="mb-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">{contractError}</div>}
          {isLoadingProposal && <div className="mb-4 rounded-lg bg-white p-4 text-sm text-gray-500">Carregando dados do orçamento...</div>}

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-2">
              {currentStep === 1 && (
                <SectionCard number="1" title="Dados do contratante">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field label="Nome completo" name="fullName" value={contractant.fullName} onChange={handleContractantChange} error={contractantErrors.identity ? 'Informe o nome completo ou a razão social.' : ''} />
                    <Field label="CPF" name="cpf" value={contractant.cpf} onChange={handleContractantChange} error={contractantErrors.document ? 'Informe o CPF ou o CNPJ.' : ''} />
                    <Field label="Data de nascimento" name="birthDate" type="date" value={contractant.birthDate} onChange={handleContractantChange} />
                    <Field label="Razão social" name="companyName" value={contractant.companyName} onChange={handleContractantChange} error={contractantErrors.identity ? 'Informe o nome completo ou a razão social.' : ''} />
                    <Field label="CNPJ" name="cnpj" value={contractant.cnpj} onChange={handleContractantChange} error={contractantErrors.document ? 'Informe o CPF ou o CNPJ.' : ''} />
                    <Field label="Telefone" name="phone" value={contractant.phone} onChange={handleContractantChange} />
                    <Field label="Endereço" name="address" value={contractant.address} onChange={handleContractantChange} />
                    <div className="relative block">
                      <span className="text-xs font-medium text-gray-500">Cidade / UF</span>
                      <input
                        type="text"
                        role="combobox"
                        aria-label="Cidade / UF"
                        aria-expanded={isCityListOpen}
                        aria-controls="city-options"
                        value={citySearch}
                        onChange={handleCitySearchChange}
                        onFocus={() => setIsCityListOpen(true)}
                        onBlur={() => setTimeout(() => setIsCityListOpen(false), 150)}
                        placeholder="Digite para buscar"
                        className={`mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-purple-400 focus:ring-2 focus:ring-purple-100 ${citySelectionError ? 'border-red-400' : 'border-gray-200'}`}
                      />
                      {isCityListOpen && (
                        <div id="city-options" role="listbox" className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                          {isLoadingCities && <div className="px-3 py-2 text-sm text-gray-500">Carregando municípios...</div>}
                          {!isLoadingCities && cityLoadError && <div className="px-3 py-2 text-sm text-red-600">{cityLoadError}</div>}
                          {!isLoadingCities && !cityLoadError && filteredCities.length === 0 && <div className="px-3 py-2 text-sm text-gray-500">Nenhum município encontrado.</div>}
                          {!isLoadingCities && !cityLoadError && filteredCities.map(city => (
                            <button
                              key={`${city.name}-${city.state}`}
                              type="button"
                              role="option"
                              aria-selected={contractant.cityState === `${city.name} - ${city.state}`}
                              onMouseDown={event => event.preventDefault()}
                              onClick={() => selectCity(city)}
                              className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-purple-50"
                            >
                              {city.name} - {city.state}
                            </button>
                          ))}
                        </div>
                      )}
                      {citySelectionError && <span className="mt-1 block text-xs text-red-600">Selecione um município da lista.</span>}
                    </div>
                    <div className="md:col-span-2"><Field label="E-mail" name="email" type="email" value={contractant.email} onChange={handleContractantChange} /></div>
                  </div>
                </SectionCard>
              )}

              {currentStep === 2 && (
                <SectionCard number="2" title="Serviços do contrato">
                  <div className="mb-3 border-b border-gray-100 pb-2 text-xs font-medium text-gray-500">
                    <span>Descrição</span>
                  </div>
                  {serviceItems.length === 0 ? (
                    <p className="py-8 text-center text-sm text-gray-500">Nenhum serviço disponível no orçamento.</p>
                  ) : serviceItems.map((service, index) => (
                    <div key={`${getServiceKey(service)}-${index}`} className="flex items-center justify-between gap-3 border-b border-gray-100 py-3 text-sm text-gray-700">
                      <span>{getServiceDescription(service)}</span>
                      <button
                        type="button"
                        aria-label={`Excluir serviço ${getServiceDescription(service)}`}
                        onClick={() => removeService(service)}
                        className="!m-0 inline-flex h-8 w-8 shrink-0 items-center justify-center !rounded-md !bg-transparent !p-0 !text-base !leading-none !text-red-600 hover:!bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-300"
                      >
                        <FaTrash aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                  <div className="relative flex justify-end pt-3">
                    <button
                      type="button"
                      aria-label="Adicionar serviço"
                      aria-expanded={isServiceListOpen}
                      onClick={() => setIsServiceListOpen(previous => !previous)}
                      className="!m-0 inline-flex h-9 w-9 items-center justify-center !rounded-md !bg-transparent !p-0 text-xl font-semibold leading-none !text-purple-700 hover:!bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-300"
                    >
                      <span aria-hidden="true">+</span>
                    </button>
                    {isServiceListOpen && (
                      <div className="absolute right-0 top-12 z-20 max-h-72 w-full max-w-xl overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                        {availableServicesToAdd.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-gray-500">Todos os serviços já foram incluídos.</div>
                        ) : availableServicesToAdd.map(service => (
                          <button
                            key={`${service.model}-${service.id}`}
                            type="button"
                            onClick={() => addService(service)}
                            className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-purple-50"
                          >
                            {service.label} ({service.model})
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </SectionCard>
              )}

              {currentStep === 3 && (
                <SectionCard number="3" title="Valores e pagamento">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="rounded-lg bg-purple-50 p-4 md:col-span-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Valor total do contrato</span>
                      </div>
                      {isEditingValue ? (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-sm text-gray-500">R$</span>
                          <input
                            autoFocus
                            type="text"
                            inputMode="decimal"
                            value={contractValueInput}
                            onChange={handleContractValueChange}
                            onBlur={() => setIsEditingValue(false)}
                            onKeyDown={event => {
                              if (event.key === 'Enter') setIsEditingValue(false);
                            }}
                            className="w-full rounded-lg border border-purple-300 bg-white px-3 py-2 text-xl font-semibold text-gray-900 outline-none focus:ring-2 focus:ring-purple-200"
                          />
                        </div>
                      ) : (
                        <div className="mt-1 flex items-center gap-2">
                          <span className="text-2xl font-semibold text-gray-900">{formatCurrency(total)}</span>
                          <button
                            type="button"
                            aria-label="Editar valor total do contrato"
                            onClick={() => setIsEditingValue(true)}
                            className="!m-0 inline-flex h-9 w-9 items-center justify-center !rounded-md !bg-transparent !p-0 !text-xl !font-bold !leading-none !text-purple-700 hover:!bg-purple-100 focus:outline-none focus:ring-2 focus:ring-purple-300"
                          >
                            <span aria-hidden="true" className="inline-block -rotate-45">✏</span>
                          </button>
                        </div>
                      )}
                    </div>
                    <label className="block"><span className="text-xs font-medium text-gray-500">Forma de pagamento</span><select name="method" value={payment.method} onChange={handlePaymentChange} className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800"><option value="">Selecione...</option><option value="avista">À vista</option><option value="pix">PIX</option><option value="cartao_credito">Cartão de crédito</option></select></label>
                    {/* <Field label="Número de parcelas" name="installments" type="number" value={payment.installments} onChange={handlePaymentChange} /> */}
                    <Field label="Vencimento da primeira parcela" name="dueDate" type="date" value={payment.dueDate} min={getCurrentDateInputValue()} onChange={handlePaymentChange} />
                    {/* <div className="md:col-span-2"><label className="block"><span className="text-xs font-medium text-gray-500">Observações</span><textarea name="notes" value={payment.notes} onChange={handlePaymentChange} rows="4" className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100" /></label></div> */}
                    {isPixPayment && (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 md:col-span-2">
                        <h4 className="mb-3 text-xs font-semibold text-gray-600">Divisão do pagamento via PIX</h4>
                        <div className={`grid grid-cols-1 gap-4 ${showThirdPixInstallment ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                          <div>
                            <span className="text-xs text-gray-500">Valor de entrada</span>
                            {isEditingPixEntry ? (
                              <div className="mt-1 flex items-center gap-2">
                                <span className="text-sm text-gray-500">R$</span>
                                <input
                                  autoFocus
                                  type="text"
                                  inputMode="decimal"
                                  value={pixEntryInput}
                                  onChange={handlePixEntryChange}
                                  onBlur={() => setIsEditingPixEntry(false)}
                                  onKeyDown={event => {
                                    if (event.key === 'Enter') setIsEditingPixEntry(false);
                                  }}
                                  className="w-full rounded-lg border border-purple-300 bg-white px-3 py-2 text-sm font-semibold text-gray-900 outline-none focus:ring-2 focus:ring-purple-200"
                                />
                              </div>
                            ) : (
                              <div className="mt-1 flex items-center gap-2">
                                <span className="rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-600">{formatCurrency(pixEntryValue)}</span>
                                <button
                                  type="button"
                                  aria-label="Editar valor de entrada"
                                  onClick={() => setIsEditingPixEntry(true)}
                                  className="!m-0 inline-flex h-9 w-9 items-center justify-center !rounded-md !bg-transparent !p-0 !text-xl !font-bold !leading-none !text-purple-700 hover:!bg-purple-100 focus:outline-none focus:ring-2 focus:ring-purple-300"
                                >
                                  <span aria-hidden="true" className="inline-block -rotate-45">✏</span>
                                </button>
                              </div>
                            )}
                          </div>
                          {showThirdPixInstallment && (
                            <div>
                              <span className="text-xs text-gray-500">2ª parcela</span>
                              {isEditingPixInstallment2 ? (
                                <div className="mt-1 flex items-center gap-2">
                                  <span className="text-sm text-gray-500">R$</span>
                                  <input
                                    autoFocus
                                    type="text"
                                    inputMode="decimal"
                                    value={pixInstallment2Input}
                                    onChange={handlePixInstallment2Change}
                                    onBlur={() => setIsEditingPixInstallment2(false)}
                                    onKeyDown={event => {
                                      if (event.key === 'Enter') setIsEditingPixInstallment2(false);
                                    }}
                                    className="w-full rounded-lg border border-purple-300 bg-white px-3 py-2 text-sm font-semibold text-gray-900 outline-none focus:ring-2 focus:ring-purple-200"
                                  />
                                </div>
                              ) : (
                                <div className="mt-1 flex items-center gap-2">
                                  <span className="rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-600">{formatCurrency(pixInstallment2Value)}</span>
                                  <button
                                    type="button"
                                    aria-label="Editar 2ª parcela"
                                    onClick={() => setIsEditingPixInstallment2(true)}
                                    className="!m-0 inline-flex h-9 w-9 items-center justify-center !rounded-md !bg-transparent !p-0 !text-xl !font-bold !leading-none !text-purple-700 hover:!bg-purple-100 focus:outline-none focus:ring-2 focus:ring-purple-300"
                                  >
                                    <span aria-hidden="true" className="inline-block -rotate-45">✏</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                          <div>
                            <span className="text-xs text-gray-500">Restante</span>
                            <div className="mt-1 flex items-center gap-2">
                              <span className="inline-block w-full cursor-not-allowed rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-600">{formatCurrency(pixRemainingValue)}</span>
                              {!showThirdPixInstallment && (
                                <button
                                  type="button"
                                  aria-label="Adicionar 3ª parcela"
                                  onClick={() => setShowThirdPixInstallment(true)}
                                  className="!m-0 inline-flex h-9 w-9 items-center justify-center !rounded-md !bg-transparent !p-0 !text-xl !font-bold !leading-none !text-purple-700 hover:!bg-purple-100 focus:outline-none focus:ring-2 focus:ring-purple-300"
                                >
                                  <span aria-hidden="true" className="inline-block">+</span>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="rounded-lg bg-gray-50 p-4 md:col-span-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-500">Prazo de entrega do projeto</span>
                      </div>
                      {isEditingProjectDeadline ? (
                        <div className="mt-2 flex items-center gap-2">
                          <input
                            autoFocus
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            aria-label="Prazo de entrega em dias úteis"
                            value={projectDeadlineInput}
                            onChange={handleProjectDeadlineChange}
                            onBlur={() => setIsEditingProjectDeadline(false)}
                            onKeyDown={event => {
                              if (event.key === 'Enter') setIsEditingProjectDeadline(false);
                            }}
                            className="w-full rounded-lg border border-purple-300 bg-white px-3 py-2 text-sm font-semibold text-gray-900 outline-none focus:ring-2 focus:ring-purple-200"
                          />
                          <span className="shrink-0 text-sm text-gray-500">dias úteis</span>
                        </div>
                      ) : (
                        <div className="mt-1 flex items-center gap-2">
                          <span className="text-lg font-semibold text-gray-900">{projectDeadlineInput || normalizedProjectDeadline || '—'}</span>
                          <span className="text-sm text-gray-500">dias úteis</span>
                          <button
                            type="button"
                            aria-label="Editar prazo de entrega do projeto"
                            onClick={() => setIsEditingProjectDeadline(true)}
                            className="!m-0 inline-flex h-9 w-9 items-center justify-center !rounded-md !bg-transparent !p-0 !text-xl !font-bold !leading-none !text-purple-700 hover:!bg-purple-100 focus:outline-none focus:ring-2 focus:ring-purple-300"
                          >
                            <span aria-hidden="true" className="inline-block -rotate-45">✏</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </SectionCard>
              )}

              {currentStep === 4 && (
                <SectionCard number="4" title="Revisão final">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="min-w-0 rounded-lg border border-gray-200 p-5"><h4 className="mb-3 text-sm font-semibold text-gray-800">Dados do contratante</h4><p className="text-sm text-gray-600">{contractant.fullName || 'Nome não preenchido'}</p><p className="mt-1 text-sm text-gray-600">{contractant.email || 'E-mail não preenchido'}</p><p className="mt-1 text-sm text-gray-600">{contractant.address || 'Endereço não preenchido'}</p></div>
                    <div className="min-w-0 overflow-hidden rounded-lg border border-gray-200 p-5" style={{ width: '100%', maxWidth: '100%', minHeight: '220px' }}><h4 className="mb-3 text-sm font-semibold text-gray-800">Serviços do contrato</h4>{serviceItems.length ? <ul className="w-full max-w-full space-y-2 text-sm text-gray-600" style={{ width: '100%', maxWidth: '100%', overflowWrap: 'anywhere', wordBreak: 'break-all', whiteSpace: 'normal' }}>{serviceItems.map((service, index) => <li key={service.id || index} className="max-w-full" style={{ width: '100%', maxWidth: '100%', overflowWrap: 'anywhere', wordBreak: 'break-all', whiteSpace: 'normal', minWidth: 0, display: 'block', lineHeight: '1.6' }}>{getServiceDescription(service)}</li>)}</ul> : <p className="text-sm text-gray-600">Nenhum serviço disponível no orçamento.</p>}</div>
                    <div className="min-w-0 rounded-lg border border-gray-200 p-5"><h4 className="mb-3 text-sm font-semibold text-gray-800">Valores</h4><p className="text-xl font-semibold text-gray-900">{formatCurrency(total)}</p></div>
                    <div className="min-w-0 rounded-lg border border-gray-200 p-5"><h4 className="mb-3 text-sm font-semibold text-gray-800">Pagamento</h4><p className="text-sm text-gray-600">{PAYMENT_METHOD_LABELS[payment.method] || 'Forma de pagamento não preenchida'}</p></div>
                  </div>
                  {completed && <div className="mt-4 rounded-lg bg-green-50 p-3 text-sm text-green-700">Contrato criado com sucesso.</div>}
                  {preparedPayload && <span className="sr-only">Contrato criado e retornado pelo backend.</span>}
                </SectionCard>
              )}

              <div className="flex items-center justify-between">
                <button type="button" onClick={goPrevious} disabled={currentStep === 1} className="!m-0 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-50">← Voltar</button>
                {currentStep < 4 ? <button type="button" onClick={goNext} className="!m-0 rounded-xl bg-purple-600 px-5 py-2 text-sm text-white hover:bg-purple-700">Continuar</button> : <button type="button" onClick={finishContract} disabled={isSavingContract || completed} className="!m-0 rounded-xl bg-purple-600 px-5 py-2 text-sm text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50">{isSavingContract ? 'Gerando...' : completed ? 'Contrato gerado' : 'Gerar contrato'}</button>}
              </div>
            </div>

            <aside className="min-w-0 rounded-2xl border border-gray-200 bg-white p-5 lg:min-h-[560px] flex flex-col">
              <h3 className="text-base font-semibold text-gray-800">Resumo do contrato</h3>
              <div className="mt-6 flex flex-1 flex-col gap-6 text-sm">
                <div>
                  <div className="text-xs text-gray-500">Contratante</div>
                  <div className="mt-1 break-words text-gray-800">{contractant.fullName || '—'}</div>
                </div>

                <div className="min-w-0">
                  <div className="text-xs text-gray-500">Serviços</div>
                  {serviceItems.length ? (
                    <ul className="mt-3 w-full max-w-full divide-y divide-gray-100 rounded-lg border border-gray-100" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                      {serviceItems.map((service, index) => (
                        <li key={service.id || index} className="max-w-full px-3 py-2 leading-5 text-gray-800" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'normal', minWidth: 0 }}>
                          {getServiceDescription(service)}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="mt-1 text-gray-800">—</div>
                  )}
                </div>

                <div className="mt-auto border-t border-gray-100 pt-6">
                  <div className="text-xs text-gray-500">Valor total</div>
                  <div className="mt-1 text-xl font-semibold text-gray-900">{formatCurrency(total)}</div>
                </div>

                <div className="border-t border-gray-100 pt-6">
                  <div className="text-xs text-gray-500">Forma de pagamento</div>
                  <div className="mt-1 break-words text-gray-800">{PAYMENT_METHOD_LABELS[payment.method] || '—'}</div>
                </div>
              </div>
            </aside>
          </div>
        </main>
      </div>
    </div>
  );
}
